/**
 * Community user profile — GET /users/me, PUT /users/{cognitoSub}.
 * PUT body mirrors iOS UserAPI.APIUserPutBody (backend-aligned, not the richer UI model).
 */

import { API_BASE_URL, buildAuthHeaders, getUserSubFromToken } from './apiClient';

export interface UserPrivacySettings {
  shareTrips: boolean;
  shareLocation: boolean;
  allowDiscovery: boolean;
  showProfile: string;
}

export interface CommunityUserProfile {
  id: string;
  username: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  bio: string | null;
  homeCity: string | null;
  ageRange: string | null;
  contactEmail: string | null;
  mobileNumber: string | null;
  whatsappNumber: string | null;
  facebook: string | null;
  instagram: string | null;
  twitter: string | null;
  threads: string | null;
  linkedin: string | null;
  snapchat: string | null;
  tiktok: string | null;
  signal: string | null;
  discord: string | null;
  cruiseCount: number;
  nightsCount: number;
  connectionCount: number;
  privacySettings: UserPrivacySettings;
}

/** Contact / social keys on PUT /users/{sub} (iOS APIUserPutBody). */
export const USER_PROFILE_CONTACT_SOCIAL_KEYS = [
  'homeCity',
  'contactEmail',
  'mobileNumber',
  'whatsappNumber',
  'facebook',
  'instagram',
  'twitter',
  'threads',
  'linkedin',
  'snapchat',
  'tiktok',
  'signal',
  'discord',
] as const;

export type UserProfileContactSocialKey =
  (typeof USER_PROFILE_CONTACT_SOCIAL_KEYS)[number];

/** Fields accepted by PUT /users/{sub} (iOS APIUserPutBody). */
export interface UserProfilePutBody {
  email: string;
  firstName: string;
  lastName: string;
  shareTrips: boolean;
  shareLocation: boolean;
  allowDiscovery: boolean;
  showProfile: string;
  cruiseCount: number;
  nightsCount: number;
  connectionCount: number;
  travelStyleTags?: string[] | null;
  bio?: string | null;
  ageRange?: string | null;
  homeCity?: string | null;
  contactEmail?: string | null;
  mobileNumber?: string | null;
  whatsappNumber?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  threads?: string | null;
  linkedin?: string | null;
  snapchat?: string | null;
  tiktok?: string | null;
  signal?: string | null;
  discord?: string | null;
}

/**
 * `undefined` keeps the current profile value; `null` or empty/whitespace clears.
 * Backend PUT is a full replace — omitted contact/social fields clear stored values.
 */
export function resolveProfilePutString(
  update: string | null | undefined,
  current: string | null | undefined
): string {
  if (update === undefined) {
    return (current ?? '').trim();
  }
  if (update == null) return '';
  return String(update).trim();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function numOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function parseCommunityUserProfile(raw: unknown): CommunityUserProfile {
  const o = asRecord(raw);
  const nested = asRecord(o.privacySettings);
  const privacy: UserPrivacySettings = {
    shareTrips: Boolean(
      nested.shareTrips ?? o.shareTrips ?? false
    ),
    shareLocation: Boolean(
      nested.shareLocation ?? o.shareLocation ?? false
    ),
    allowDiscovery: Boolean(
      nested.allowDiscovery ?? o.allowDiscovery ?? false
    ),
    showProfile:
      strOrNull(nested.showProfile ?? o.showProfile) || 'CONNECTIONS_ONLY',
  };

  const email = strOrNull(o.email) || '';
  const username = strOrNull(o.username) || email;
  const firstName = strOrNull(o.firstName);
  const lastName = strOrNull(o.lastName);
  let displayName = strOrNull(o.displayName);
  if (!displayName) {
    const full = [firstName, lastName].filter(Boolean).join(' ').trim();
    displayName = full || null;
  }

  return {
    id: strOrNull(o.id) || '',
    username,
    email,
    firstName,
    lastName,
    displayName,
    bio: strOrNull(o.bio),
    homeCity: strOrNull(o.homeCity),
    ageRange: strOrNull(o.ageRange),
    contactEmail: strOrNull(o.contactEmail),
    mobileNumber: strOrNull(o.mobileNumber),
    whatsappNumber: strOrNull(o.whatsappNumber),
    facebook: strOrNull(o.facebook),
    instagram: strOrNull(o.instagram),
    twitter: strOrNull(o.twitter),
    threads: strOrNull(o.threads),
    linkedin: strOrNull(o.linkedin),
    snapchat: strOrNull(o.snapchat),
    tiktok: strOrNull(o.tiktok),
    signal: strOrNull(o.signal),
    discord: strOrNull(o.discord),
    cruiseCount: numOrZero(o.cruiseCount),
    nightsCount: numOrZero(o.nightsCount),
    connectionCount: numOrZero(o.connectionCount),
    privacySettings: privacy,
  };
}

export type UserProfilePutUpdates = {
  firstName: string;
  lastName: string;
  bio: string | null;
  ageRange: string | null;
  privacy: UserPrivacySettings;
} & Partial<Record<UserProfileContactSocialKey, string | null>>;

export function buildUserProfilePutBody(
  profile: CommunityUserProfile,
  updates: UserProfilePutUpdates
): UserProfilePutBody {
  const contactSocial = USER_PROFILE_CONTACT_SOCIAL_KEYS.reduce(
    (acc, key) => {
      acc[key] = resolveProfilePutString(updates[key], profile[key]);
      return acc;
    },
    {} as Record<UserProfileContactSocialKey, string>
  );

  return {
    email: profile.email,
    firstName: updates.firstName.trim(),
    lastName: updates.lastName.trim(),
    shareTrips: updates.privacy.shareTrips,
    shareLocation: updates.privacy.shareLocation,
    allowDiscovery: updates.privacy.allowDiscovery,
    showProfile: updates.privacy.showProfile || 'CONNECTIONS_ONLY',
    cruiseCount: profile.cruiseCount,
    nightsCount: profile.nightsCount,
    connectionCount: profile.connectionCount,
    travelStyleTags: null,
    bio: updates.bio,
    ageRange: updates.ageRange,
    ...contactSocial,
  };
}

export async function getCurrentUserProfile(): Promise<CommunityUserProfile> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    throw new Error(`getCurrentUserProfile failed: ${response.status}`);
  }
  return parseCommunityUserProfile(await response.json());
}

/** GET /users/{id} — Cognito sub for other users (iOS UserAPI.getUser). */
export async function getUserById(userId: string): Promise<CommunityUserProfile> {
  const id = String(userId ?? '').trim();
  if (!id) {
    throw new Error('getUserById: userId is required');
  }
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    throw new Error(`getUserById failed: ${response.status}`);
  }
  return parseCommunityUserProfile(await response.json());
}

/** Prefer displayName, then username, then empty. */
export function labelFromCommunityProfile(profile: CommunityUserProfile): string {
  const name = profile.displayName?.trim();
  if (name) return name;
  const username = profile.username?.trim();
  if (username) return username;
  return '';
}

export async function putCurrentUserProfile(
  body: UserProfilePutBody
): Promise<CommunityUserProfile> {
  const sub = getUserSubFromToken();
  if (!sub) {
    throw new Error('putCurrentUserProfile: missing Cognito sub');
  }
  const headers = await buildAuthHeaders(true);
  const response = await fetch(
    `${API_BASE_URL}/users/${encodeURIComponent(sub)}`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`putCurrentUserProfile failed: ${response.status}`);
  }
  return parseCommunityUserProfile(await response.json());
}
